import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { apiService, ApiError } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff, KeyRound, Loader2, ShieldCheck } from 'lucide-react';

const PASSWORD_MIN_LENGTH = 12;
const PASSWORD_LETTER_REGEX = /[A-Za-z]/;
const PASSWORD_NUMBER_REGEX = /\d/;

const validatePassword = (value: string): string | null => {
  if (value.length < PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters`;
  }
  if (!PASSWORD_LETTER_REGEX.test(value)) {
    return 'Password must include at least one letter';
  }
  if (!PASSWORD_NUMBER_REGEX.test(value)) {
    return 'Password must include at least one number';
  }
  return null;
};

export function UserPasswordAuth() {
  const { user } = useAuth();
  const [setOtp, setSetOtp] = useState('');
  const [setPassword, setSetPassword] = useState('');
  const [setPasswordConfirm, setSetPasswordConfirm] = useState('');
  const [setMessage, setSetMessage] = useState('');
  const [setError, setSetError] = useState('');
  const [isSendingSetOtp, setIsSendingSetOtp] = useState(false);
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [showSetPasswordConfirm, setShowSetPasswordConfirm] = useState(false);

  const [resetPhoneNumber, setResetPhoneNumber] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');
  const [isSendingResetOtp, setIsSendingResetOtp] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetPasswordConfirm, setShowResetPasswordConfirm] = useState(false);

  const [checkPasswordValue, setCheckPasswordValue] = useState('');
  const [checkMessage, setCheckMessage] = useState('');
  const [checkError, setCheckError] = useState('');
  const [isCheckingPassword, setIsCheckingPassword] = useState(false);
  const [showCheckPassword, setShowCheckPassword] = useState(false);

  useEffect(() => {
    if (user?.phoneNumber) {
      setResetPhoneNumber(user.phoneNumber);
    }
  }, [user?.phoneNumber]);

  const sendOtp = async (phoneNumber: string, type: 'set' | 'reset') => {
    if (!phoneNumber.trim()) {
      if (type === 'set') setSetError('Phone number is missing');
      else setResetError('Phone number is required');
      return;
    }

    if (type === 'set') {
      setIsSendingSetOtp(true);
      setSetError('');
      setSetMessage('');
    } else {
      setIsSendingResetOtp(true);
      setResetError('');
      setResetMessage('');
    }

    try {
      await apiService.sendSms({ phoneNumber: phoneNumber.trim() });
      if (type === 'set') {
        setSetMessage('OTP sent successfully');
      } else {
        setResetMessage('OTP sent successfully');
      }
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Failed to send OTP';
      if (type === 'set') setSetError(message);
      else setResetError(message);
    } finally {
      if (type === 'set') setIsSendingSetOtp(false);
      else setIsSendingResetOtp(false);
    }
  };

  const handleSetPassword = async () => {
    setSetError('');
    setSetMessage('');

    if (!setOtp.trim()) {
      setSetError('OTP is required');
      return;
    }

    const passwordError = validatePassword(setPassword);
    if (passwordError) {
      setSetError(passwordError);
      return;
    }

    if (setPassword !== setPasswordConfirm) {
      setSetError('Passwords do not match');
      return;
    }

    setIsSettingPassword(true);
    try {
      const response = await apiService.setUserPassword({
        otp: setOtp.trim(),
        newPassword: setPassword,
      });
      setSetMessage(response.message || 'Password has been set successfully');
      setSetOtp('');
      setSetPassword('');
      setSetPasswordConfirm('');
    } catch (error) {
      setSetError(error instanceof ApiError ? error.message : 'Failed to set password');
    } finally {
      setIsSettingPassword(false);
    }
  };

  const handleResetPassword = async () => {
    setResetError('');
    setResetMessage('');

    if (!resetPhoneNumber.trim()) {
      setResetError('Phone number is required');
      return;
    }

    if (!resetOtp.trim()) {
      setResetError('OTP is required');
      return;
    }

    const passwordError = validatePassword(resetPasswordValue);
    if (passwordError) {
      setResetError(passwordError);
      return;
    }

    if (resetPasswordValue !== resetPasswordConfirm) {
      setResetError('Passwords do not match');
      return;
    }

    setIsResettingPassword(true);
    try {
      const response = await apiService.resetPassword({
        phoneNumber: resetPhoneNumber.trim(),
        otp: resetOtp.trim(),
        newPassword: resetPasswordValue,
      });
      setResetMessage(response.message || 'Password has been reset successfully');
      setResetOtp('');
      setResetPasswordValue('');
      setResetPasswordConfirm('');
    } catch (error) {
      setResetError(error instanceof ApiError ? error.message : 'Failed to reset password');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleCheckPassword = async () => {
    setCheckError('');
    setCheckMessage('');

    if (!user?.phoneNumber) {
      setCheckError('User phone number is missing');
      return;
    }

    if (!checkPasswordValue) {
      setCheckError('Password is required');
      return;
    }

    setIsCheckingPassword(true);
    try {
      await apiService.loginWithPassword({
        phoneNumber: user.phoneNumber,
        password: checkPasswordValue,
      });
      setCheckMessage('Password is valid');
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setCheckError('Invalid password or password not set');
      } else {
        setCheckError(error instanceof ApiError ? error.message : 'Failed to check password');
      }
    } finally {
      setIsCheckingPassword(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-2">
          <KeyRound className="h-5 w-5 text-gray-700" />
          <h2 className="text-xl font-semibold text-gray-900">Password Authentication</h2>
        </div>
        <p className="text-sm text-gray-500">
          Password must be at least 12 characters and include at least one letter and one number.
        </p>
      </div>

      <Accordion type="single" collapsible defaultValue="set-password" className="space-y-4">
        <AccordionItem value="set-password" className="bg-white border border-gray-200 rounded-xl px-6">
          <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:no-underline">
            Set Password
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pb-4">
              <p className="text-sm text-gray-500">Send OTP to your phone, then set a new password for phone + password login.</p>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => sendOtp(user?.phoneNumber || '', 'set')}
                  disabled={isSendingSetOtp || !user?.phoneNumber}
                >
                  {isSendingSetOtp ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Send OTP
                </Button>
                <span className="text-sm text-gray-500">{user?.phoneNumber || 'No phone number'}</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input placeholder="OTP" value={setOtp} onChange={(e) => setSetOtp(e.target.value)} />
                <div className="relative">
                  <Input
                    type={showSetPassword ? 'text' : 'password'}
                    placeholder="New password"
                    value={setPassword}
                    onChange={(e) => setSetPassword(e.target.value)}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSetPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    aria-label={showSetPassword ? 'Hide password' : 'Show password'}
                  >
                    {showSetPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <div className="relative">
                  <Input
                    type={showSetPasswordConfirm ? 'text' : 'password'}
                    placeholder="Confirm password"
                    value={setPasswordConfirm}
                    onChange={(e) => setSetPasswordConfirm(e.target.value)}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSetPasswordConfirm((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    aria-label={showSetPasswordConfirm ? 'Hide password' : 'Show password'}
                  >
                    {showSetPasswordConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button onClick={handleSetPassword} disabled={isSettingPassword}>
                {isSettingPassword ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Set Password
              </Button>

              {setError && <p className="text-sm text-red-600">{setError}</p>}
              {setMessage && <p className="text-sm text-green-600">{setMessage}</p>}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="reset-password" className="bg-white border border-gray-200 rounded-xl px-6">
          <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:no-underline">
            Reset Password
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pb-4">
              <p className="text-sm text-gray-500">Forgot password flow using phone number + OTP.</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  placeholder="Phone number"
                  value={resetPhoneNumber}
                  onChange={(e) => setResetPhoneNumber(e.target.value)}
                />
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => sendOtp(resetPhoneNumber, 'reset')} disabled={isSendingResetOtp}>
                    {isSendingResetOtp ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                    Send OTP
                  </Button>
                </div>
                <Input placeholder="OTP" value={resetOtp} onChange={(e) => setResetOtp(e.target.value)} />
                <div className="relative">
                  <Input
                    type={showResetPassword ? 'text' : 'password'}
                    placeholder="New password"
                    value={resetPasswordValue}
                    onChange={(e) => setResetPasswordValue(e.target.value)}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    aria-label={showResetPassword ? 'Hide password' : 'Show password'}
                  >
                    {showResetPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <div className="relative">
                  <Input
                    type={showResetPasswordConfirm ? 'text' : 'password'}
                    placeholder="Confirm password"
                    value={resetPasswordConfirm}
                    onChange={(e) => setResetPasswordConfirm(e.target.value)}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetPasswordConfirm((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    aria-label={showResetPasswordConfirm ? 'Hide password' : 'Show password'}
                  >
                    {showResetPasswordConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button onClick={handleResetPassword} disabled={isResettingPassword}>
                {isResettingPassword ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Reset Password
              </Button>

              {resetError && <p className="text-sm text-red-600">{resetError}</p>}
              {resetMessage && <p className="text-sm text-green-600">{resetMessage}</p>}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="check-password" className="bg-white border border-gray-200 rounded-xl px-6">
          <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:no-underline">
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-gray-700" />
              Check Password
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pb-4">
              <p className="text-sm text-gray-500">Validate whether the entered password works for current user phone number.</p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input value={user?.phoneNumber || ''} disabled />
                <div className="relative">
                  <Input
                    type={showCheckPassword ? 'text' : 'password'}
                    placeholder="Password to check"
                    value={checkPasswordValue}
                    onChange={(e) => setCheckPasswordValue(e.target.value)}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCheckPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    aria-label={showCheckPassword ? 'Hide password' : 'Show password'}
                  >
                    {showCheckPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <Button onClick={handleCheckPassword} disabled={isCheckingPassword}>
                  {isCheckingPassword ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Check Password
                </Button>
              </div>

              {checkError && <p className="text-sm text-red-600">{checkError}</p>}
              {checkMessage && <p className="text-sm text-green-600">{checkMessage}</p>}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
